import styled from 'styled-components'
import { colorPalette } from '../../../../style/colorPalette'
import { StatusColumnCumulationData } from '../../Kanban'
import { Typography } from '@mui/material'
import { intl } from '../../../../../i18n'
import { memo } from 'react'
import { roundNumber } from '../../../../../utils/number'

export type Props = {
  title: string
  cumulationData: StatusColumnCumulationData
  backgroundColor?: string
}

export const StatusColumnHeader = memo(
  ({ title, cumulationData, backgroundColor }: Props) => {
    const { count, totalCount, estimatedHour, actualHour } = cumulationData
    const formattedEstimatedHour = roundNumber(estimatedHour, 2)
    const formattedActualHour = roundNumber(actualHour, 2)
    const percentage =
      totalCount > 0 ? roundNumber((count / totalCount) * 100, 2) : 0
    const dotColor = backgroundColor ?? colorPalette.monotone[4]
    return (
      <Header>
        <TitleRow>
          <ColorDot color={dotColor} />
          <Title variant="h5">{title}</Title>
          <Cumulation
            variant="h6"
            style={{ alignSelf: 'flex-end', marginBottom: '3px' }}
          >
            {`${intl.formatMessage(
              { id: 'kanban.column.header.hitShort' },
              { total: count }
            )} (${percentage}%)`}
          </Cumulation>
        </TitleRow>
        <DataRow>
          <Cumulation variant="h6">
            {`${intl.formatMessage(
              { id: 'kanban.column.header.estimatedHour' },
              { estimatedHour: formattedEstimatedHour }
            )} / ${intl.formatMessage(
              { id: 'kanban.column.header.actualHour' },
              { actualHour: formattedActualHour }
            )}`}
          </Cumulation>
        </DataRow>
      </Header>
    )
  }
)

const Header = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '8px',
  width: '100%',
})

const TitleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const ColorDot = styled('div')<{ color: string }>(props => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: props.color,
  flexShrink: 0,
}))

const DataRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const Title = styled(Typography)({
  fontWeight: '700',
  color: colorPalette.monotone[10],
})

const Cumulation = styled(Typography)({
  fontWeight: '500',
  color: colorPalette.monotone[10],
  fontSize: '14px',
})
